import React, {
  useState,
  useCallback,
  useMemo,
  useEffect,
  useRef,
} from 'react';
import {
  Steps,
  Card,
  Button,
  Form,
  Input,
  Select,
  Upload,
  Space,
  Divider,
  Typography,
  Alert,
  Progress,
  Spin,
  Modal,
  Table,
  Tag,
  message,
  Radio,
  Checkbox,
  Switch,
  InputNumber,
  Row,
  Col,
  Tooltip,
  List,
  Collapse,
  Statistic,
} from 'antd';
import {
  FileTextOutlined,
  FilePdfOutlined,
  LinkOutlined,
  FileExcelOutlined,
  UploadOutlined,
  CloudDownloadOutlined,
  EditOutlined,
  CheckCircleOutlined,
  ExclamationCircleOutlined,
  CloseCircleOutlined,
  InfoCircleOutlined,
  SettingOutlined,
  FileAddOutlined,
  FileImageOutlined,
  GlobalOutlined,
} from '@ant-design/icons';
import { v2ToppingsApi } from '../../../../api';

const { Step } = Steps;
const { Option } = Select;
const { TextArea } = Input;
const { Title, Text, Paragraph } = Typography;
const { Dragger } = Upload;
const { Panel } = Collapse;

const AddToppingWizard = ({ visible, onCancel, onSuccess }) => {
  // 状態管理
  const [currentStep, setCurrentStep] = useState(0);
  const [form] = Form.useForm();
  const modalBodyRef = useRef(null);
  const [sourceType, setSourceType] = useState(null);

  // スムーススクロール関数
  const scrollToTop = () => {
    setTimeout(() => {
      if (modalBodyRef.current) {
        const scrollContainer = modalBodyRef.current.parentElement;
        if (scrollContainer) {
          scrollContainer.scrollTo({
            top: 0,
            behavior: 'smooth',
          });
        }
      }
    }, 300);
  };

  const scrollToBottom = () => {
    setTimeout(() => {
      if (modalBodyRef.current) {
        const scrollContainer = modalBodyRef.current.parentElement;
        if (scrollContainer) {
          scrollContainer.scrollTo({
            top: scrollContainer.scrollHeight,
            behavior: 'smooth',
          });
        }
      }
    }, 300);
  };
  const [loading, setLoading] = useState(false);
  const [extractionResults, setExtractionResults] = useState(null);
  const [previewData, setPreviewData] = useState(null);
  const [urlExtractionLoading, setUrlExtractionLoading] = useState(false);
  const [availableTags, setAvailableTags] = useState([]); // ユーザーのタグ一覧
  const [tagsLoading, setTagsLoading] = useState(false);
  const [crawlSettings, setCrawlSettings] = useState({
    crawl_subpages: false,
    max_depth: 2,
    limit: 20,
  });
  const [accordionActiveKey, setAccordionActiveKey] = useState([]);

  // ユニット見積もり
  const [estimatedUnits, setEstimatedUnits] = useState(null);

  // フォームデータを独立して管理
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    category: '',
    tags: [],
    content: '',
    strength: 1.0,
    chunk_size: 1000,
    chunk_overlap: 200,
  });

  // カテゴリの日本語表示名を取得
  const getCategoryDisplayName = category => {
    const categoryMap = {
      'cta': 'CTA・行動喚起',
      'product': '商品・サービス情報',
      'case_study': '事例・実績',
      'faq': 'よくある質問',
      'statistics': '統計・データ',
      'testimonial': '口コミ・評価',
      'company': '会社情報',
      'support': 'サポート・問い合わせ',
      'other': 'その他',
    };
    return categoryMap[category] || category;
  };

  // ユーザーのタグを取得
  const fetchUserTags = useCallback(async () => {
    try {
      setTagsLoading(true);
      const response = await v2ToppingsApi.getTags();
      setAvailableTags(response.data?.tags || []);
    } catch (error) {
      console.error('Failed to fetch user tags:', error);
    } finally {
      setTagsLoading(false);
    }
  }, []);

  // モーダルが開かれたときにタグを取得
  useEffect(() => {
    if (visible) {
      fetchUserTags();
    }
  }, [visible, fetchUserTags]);

  // リセット関数
  const resetWizard = useCallback(() => {
    setCurrentStep(0);
    setSourceType(null);
    setExtractionResults(null);
    setPreviewData(null);
    setUrlExtractionLoading(false);
    setAccordionActiveKey([]);
    setEstimatedUnits(null);
    form.resetFields();
    setFormData({
      title: '',
      description: '',
      category: '',
      tags: [],
      content: '',
      strength: 1.0,
      chunk_size: 1000,
      chunk_overlap: 200,
    });
  }, [form]);

  // モーダルクローズ時の処理
  const handleClose = useCallback(() => {
    resetWizard();
    onCancel();
  }, [resetWizard, onCancel]);

  // ファイル変更ハンドラ（実際のAPI呼び出し）
  const handleFileChange = useCallback(
    async info => {
      const { file } = info;
      if (file.status !== 'removed') {
        try {
          setLoading(true);
          const formData = new FormData();
          formData.append('file', file.originFileObj || file);
          formData.append(
            'title',
            form.getFieldValue('title') || file.name.replace(/\.[^/.]+$/, ''),
          );
          formData.append(
            'description',
            form.getFieldValue('description') || '',
          );
          formData.append('category', form.getFieldValue('category') || '');
          formData.append(
            'tags',
            JSON.stringify(form.getFieldValue('tags') || []),
          );
          formData.append('save_immediately', 'false'); // プレビューのみ、DB保存はしない

          // プレビュー用APIを呼び出し（DB保存なし）
          const response = await v2ToppingsApi.uploadWithPreview(formData);

          if (!response.data) {
            throw new Error('プレビューデータの取得に失敗しました');
          }

          const result = response.data;
          console.log('Preview result:', result); // デバッグログ

          // プレビューデータを設定（DB保存はしない）
          if (result.success && result.preview) {
            const preview = result.preview;
            const processingResult = result.processing_result;

            // 構造化データを取得（バックエンドから直接使用）
            const structuredData =
              processingResult?.structured_data || preview.structured_data;

            // ユニット情報はバックエンドで計算されたものを使用
            if (preview.estimated_units !== undefined) {
              // バックエンドから計算済みのユニット情報を使用
              setEstimatedUnits({
                units: preview.estimated_units,
                details: preview.unit_details || '',
              });
            } else {
              // フォールバック: フロントエンドで計算
              const content =
                processingResult?.content || preview.content || '';
              const calculateUnits = () => {
                if (sourceType === 'csv') {
                  const rowCount =
                    structuredData?.total_rows || content.split('\n').length;
                  return {
                    units: rowCount > 0 ? Math.ceil(rowCount / 100) : 1,
                    details: `${rowCount.toLocaleString()}行`,
                  };
                } else if (sourceType === 'pdf') {
                  const pageCount = structuredData?.pages || 0;
                  return {
                    units: pageCount > 0 ? Math.ceil(pageCount / 5) : 1,
                    details: `${pageCount}ページ`,
                  };
                } else {
                  const charCount = content.length;
                  return {
                    units: charCount > 0 ? Math.ceil(charCount / 5000) : 1,
                    details: `${charCount.toLocaleString()}文字`,
                  };
                }
              };
              setEstimatedUnits(calculateUnits());
            }

            setPreviewData({
              file_info: preview.file_info,
              content_stats:
                processingResult?.content_stats || preview.content_stats,
              content_preview: preview.content_preview,
              structured_data: structuredData,
              raw_content: processingResult?.content || preview.content || '', // 最終登録時に使用
              processing_result: processingResult, // 最終登録時に使用
              status: 'preview', // プレビュー状態
            });

            // ファイル解析完了時にモーダルを下までスムーススクロール
            scrollToBottom();

            // message.success('ファイルの解析が完了しました'); // 削除：二重表示を防ぐ
          } else {
            throw new Error('プレビューの生成に失敗しました');
          }
        } catch (error) {
          console.error('File preview error:', error);
          message.error(`ファイルの解析に失敗しました: ${error.message}`);
        } finally {
          setLoading(false);
        }
      }
    },
    [form],
  );

  // ステップ1: ソースタイプ選択
  const SourceTypeStep = () => {
    const sourceTypes = [
      {
        type: 'csv',
        title: 'CSV ファイル',
        description: 'データテーブルやリストを構造化して取り込み',
        icon: <FileExcelOutlined style={{ fontSize: 24, color: '#52c41a' }} />,
      },
      {
        type: 'pdf',
        title: 'PDF ファイル',
        description: '文書からテキストを自動抽出',
        icon: <FilePdfOutlined style={{ fontSize: 24, color: '#ff4d4f' }} />,
      },
      {
        type: 'url',
        title: 'Web ページ',
        description: 'URLからコンテンツを高品質で抽出',
        icon: <LinkOutlined style={{ fontSize: 24, color: '#1890ff' }} />,
      },
      {
        type: 'text',
        title: 'テキスト入力',
        description: 'リッチエディタで直接コンテンツを作成',
        icon: <EditOutlined style={{ fontSize: 24, color: '#722ed1' }} />,
      },
    ];

    const handleSourceTypeSelect = type => {
      setSourceType(type);
      form.setFieldsValue({ source_type: type });
      // 自動的に次のステップに進む
      setTimeout(() => {
        setCurrentStep(1);
      }, 300); // 少し遅延を入れてスムーズに見せる
    };

    return (
      <div>
        <Title
          level={5}
          style={{ textAlign: 'center', marginBottom: 32, color: '#1890ff' }}
        >
          独自情報のソースを選択
        </Title>

        <Row gutter={[16, 16]}>
          {sourceTypes.map(type => (
            <Col span={12} key={type.type}>
              <Card
                hoverable
                className={
                  sourceType === type.type ? 'selected-source-type' : ''
                }
                onClick={() => handleSourceTypeSelect(type.type)}
                style={{
                  border:
                    sourceType === type.type
                      ? '2px solid #1890ff'
                      : '1px solid #d9d9d9',
                  borderRadius: 8,
                }}
              >
                <div style={{ textAlign: 'center', padding: 16 }}>
                  {type.icon}
                  <Title level={5} style={{ margin: '16px 0 8px' }}>
                    {type.title}
                  </Title>
                  <Paragraph type="secondary" style={{ margin: 0 }}>
                    {type.description}
                  </Paragraph>
                </div>
              </Card>
            </Col>
          ))}
        </Row>
      </div>
    );
  };

  // ステップ2: 基本情報入力
  const BasicInfoStep = () => {
    return (
      <div>
        <Title level={5} style={{ marginBottom: 24, color: '#1890ff' }}>
          基本情報の入力
        </Title>

        <Form form={form} layout="vertical" size="large">
          {sourceType !== 'url' && (
            <Form.Item
              label="タイトル"
              name="title"
              rules={[
                { required: true, message: 'タイトルを入力してください' },
                {
                  max: 200,
                  message: 'タイトルは200文字以内で入力してください',
                },
              ]}
            >
              <Input placeholder="独自情報のタイトルを入力" />
            </Form.Item>
          )}

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item label="カテゴリ" name="category">
                <Select placeholder="カテゴリを選択" allowClear>
                  <Option value="cta">CTA・行動喚起</Option>
                  <Option value="product">商品・サービス情報</Option>
                  <Option value="case_study">事例・実績</Option>
                  <Option value="faq">よくある質問</Option>
                  <Option value="statistics">統計・データ</Option>
                  <Option value="testimonial">口コミ・評価</Option>
                  <Option value="company">会社情報</Option>
                  <Option value="support">サポート・問い合わせ</Option>
                  <Option value="other">その他</Option>
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item label="タグ" name="tags">
                <Select
                  mode="tags"
                  placeholder="タグを入力（複数可）"
                  style={{ width: '100%' }}
                  loading={tagsLoading}
                  notFoundContent={
                    tagsLoading ? <Spin size="small" /> : '新しいタグを入力'
                  }
                >
                  {availableTags.map(tag => (
                    <Option key={tag} value={tag}>
                      {tag}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Form.Item label="説明（任意）" name="description">
            <TextArea
              rows={3}
              placeholder="この独自情報の説明を入力（任意）"
              maxLength={500}
              showCount
            />
          </Form.Item>

          {/* ソースタイプ別の入力項目 */}
          {sourceType === 'csv' || sourceType === 'pdf' ? (
            <FileUploadSection />
          ) : sourceType === 'url' ? (
            <UrlInputSection />
          ) : sourceType === 'text' ? (
            <RichTextSection />
          ) : null}
        </Form>
      </div>
    );
  };

  // ファイルアップロードセクション
  const FileUploadSection = () => {
    const uploadProps = useMemo(
      () => ({
        name: 'file',
        multiple: false,
        accept: sourceType === 'csv' ? '.csv' : '.pdf',
        beforeUpload: () => false, // 自動アップロードを無効化
        onChange: handleFileChange,
      }),
      [sourceType, handleFileChange],
    );

    return (
      <div>
        <Form.Item
          label={`${sourceType.toUpperCase()} ファイル`}
          name="file"
          valuePropName="fileList"
          getValueFromEvent={e => e && e.fileList}
          rules={[{ required: true, message: 'ファイルを選択してください' }]}
        >
          <Dragger {...uploadProps} style={{ padding: '20px' }}>
            <p className="ant-upload-drag-icon">
              {sourceType === 'csv' ? (
                <FileExcelOutlined />
              ) : (
                <FilePdfOutlined />
              )}
            </p>
            <p className="ant-upload-text">
              ファイルをドラッグ＆ドロップ、またはクリックして選択
            </p>
            <p className="ant-upload-hint">
              {sourceType === 'csv' ? 'CSV形式のファイル' : 'PDF形式のファイル'}
              のみサポート
            </p>
          </Dragger>
        </Form.Item>

        {loading && (
          <div style={{ textAlign: 'center', padding: '20px' }}>
            <Spin size="large" />
            <div style={{ marginTop: 16 }}>
              <Text>ファイルを解析中...</Text>
            </div>
          </div>
        )}

        {previewData && <FilePreviewSection preview={previewData} />}
      </div>
    );
  };

  // URL入力セクション
  const UrlInputSection = () => {
    const handleUrlExtraction = async () => {
      try {
        const urls = form.getFieldValue('urls') || '';
        const urlList = urls.split('\n').filter(url => url.trim());

        if (urlList.length === 0) {
          message.error('URLを入力してください');
          return;
        }

        // URL重複チェック（入力内での重複のみ）
        const uniqueUrls = [...new Set(urlList)];
        if (uniqueUrls.length !== urlList.length) {
          message.warning(
            `入力内重複URL ${urlList.length - uniqueUrls.length}件を除外しました`,
          );
        }

        const finalUrlList = uniqueUrls;

        setUrlExtractionLoading(true);

        // FireCrawlの高度なURL抽出を使用
        try {
          const extractRequest = {
            urls: finalUrlList,
            crawl_subpages: crawlSettings.crawl_subpages,
            max_depth: crawlSettings.max_depth,
            limit: crawlSettings.limit,
          };

          console.log('FireCrawl extraction request:', extractRequest);

          const response = await v2ToppingsApi.extractUrls(extractRequest);
          const firecrawlResult = response.data;
          console.log('FireCrawl result:', firecrawlResult);

          // FireCrawl結果を処理（元のステータスを保持）
          const results = firecrawlResult.results || [];
          const successCount = results.filter(
            r => r.status === 'success',
          ).length;
          const skippedCountLocal = results.filter(
            r => r.status === 'skipped_duplicate',
          ).length;
          const errorCountLocal = results.filter(
            r => r.status === 'error',
          ).length;
          const failCount =
            (firecrawlResult.errors ? firecrawlResult.errors.length : 0) +
            errorCountLocal;

          setExtractionResults({
            results: results, // 元のステータスをそのまま使用
            summary: {
              ...firecrawlResult.summary, // バックエンドのサマリーをそのまま使用
              total_urls: finalUrlList.length,
              extraction_method: crawlSettings.crawl_subpages
                ? 'firecrawl_crawl'
                : 'firecrawl_scrape',
              crawled_pages: crawlSettings.crawl_subpages ? results.length : 0,
            },
          });

          // URL抽出成功時にユニット見積もりを設定（1URL = 1ユニット）
          if (successCount > 0) {
            setEstimatedUnits({
              units: successCount,
              details: `${successCount}件のURL`,
            });
          }

          const summary = firecrawlResult.summary || {};
          const seedUrls = summary.total_seed_urls || finalUrlList.length;
          const extractedUrls = summary.total_extracted_urls || results.length;
          const skippedCountSummary = summary.skipped_duplicates || 0;
          const errorCountSummary = summary.failed_extractions || 0;

          if (successCount > 0) {
            const crawlMessage = crawlSettings.crawl_subpages
              ? `（${seedUrls}個のシードURLから${extractedUrls}件のURLを発見）`
              : '';
            const skipMessage =
              skippedCountSummary > 0
                ? `、${skippedCountSummary}件は重複スキップ`
                : '';
            const errorMessage =
              errorCountSummary > 0 ? `、${errorCountSummary}件は抽出失敗` : '';
            message.success(
              `${successCount}件のURLコンテンツを抽出完了${skipMessage}${errorMessage}${crawlMessage}`,
            );

            // URL抽出完了時にモーダルを下までスムーススクロール
            scrollToBottom();
          } else {
            if (skippedCountSummary > 0 && errorCountSummary === 0) {
              message.warning(
                `${extractedUrls}件のURLが発見されましたが、すべて重複のためスキップされました`,
              );
            } else if (errorCountSummary > 0) {
              message.error(
                `抽出処理は実行されましたが、${errorCountSummary}件で抽出に失敗し、${skippedCountSummary}件は重複スキップされました`,
              );
            } else {
              message.error('URLの抽出処理に失敗しました');
            }
          }
        } catch (firecrawlError) {
          console.warn(
            'FireCrawl failed, falling back to basic scraping:',
            firecrawlError,
          );

          // フォールバック：基本的なスクレイピング
          const results = [];

          for (let i = 0; i < finalUrlList.length; i++) {
            const url = finalUrlList[i].trim();

            try {
              message.loading(
                `${i + 1}/${finalUrlList.length} URLを処理中: ${url}`,
                0,
              );

              const response = await v2ToppingsApi.createToppingFromUrl({
                title: `URL: ${url}`,
                description: '',
                category: form.getFieldValue('category') || '',
                tags: form.getFieldValue('tags') || [],
                type: 'url',
                raw_data: url,
                source_url: url,
                rag_enabled: true,
                rag_chunk_size: 1000,
                rag_overlap: 200,
                strength: 1.0,
              });

              message.destroy();

              const result = response.data;

              let actualContentLength = 0;
              const toppingId = result.id || result.topping_id;

              if (toppingId) {
                try {
                  const detailResponse =
                    await v2ToppingsApi.getTopping(toppingId);
                  actualContentLength =
                    detailResponse.data?.content?.length || 0;
                } catch (error) {
                  console.warn('Failed to fetch content length:', error);
                }
              }

              results.push({
                source_url: url,
                title: result.title || `URL: ${url}`,
                content_length: actualContentLength,
                topping_id: toppingId,
                status: 'success',
                extraction_method: 'basic_scraping',
              });
            } catch (error) {
              message.destroy();
              console.error(`URL extraction error for ${url}:`, error);
              results.push({
                source_url: url,
                title: `エラー: ${url}`,
                content_length: 0,
                status: 'error',
                error: error.message,
                extraction_method: 'basic_scraping',
              });
            }
          }

          const successCount = results.filter(
            r => r.status === 'success',
          ).length;
          const failCount = results.filter(r => r.status === 'error').length;

          setExtractionResults({
            results,
            summary: {
              total_urls: finalUrlList.length,
              successful_extractions: successCount,
              failed_extractions: failCount,
              total_content_length: results.reduce(
                (sum, r) => sum + r.content_length,
                0,
              ),
              total_credits_used: 0,
              extraction_method: 'basic_scraping',
            },
          });

          if (successCount > 0) {
            message.success(
              `${successCount}件のURLから抽出・保存完了（フォールバック）`,
            );
          } else {
            message.error('すべてのURLで抽出に失敗しました');
          }
        }
      } catch (error) {
        message.destroy();
        console.error('URL extraction error:', error);
        message.error(`URL抽出に失敗しました: ${error.message}`);
      } finally {
        setUrlExtractionLoading(false);
      }
    };

    return (
      <div>
        <Form.Item
          label="URL"
          name="urls"
          rules={[{ required: true, message: 'URLを入力してください' }]}
        >
          <TextArea rows={4} placeholder="URLを入力（改行で複数入力可能）" />
        </Form.Item>

        <Collapse
          ghost
          activeKey={accordionActiveKey}
          onChange={keys => setAccordionActiveKey(keys)}
        >
          <Panel
            header={
              <Space>
                <SettingOutlined />
                高度な設定
              </Space>
            }
            key="advanced"
          >
            <div
              onClick={e => {
                if (e && typeof e.stopPropagation === 'function') {
                  e.stopPropagation();
                }
              }}
              onMouseDown={e => {
                if (e && typeof e.stopPropagation === 'function') {
                  e.stopPropagation();
                }
              }}
              onMouseUp={e => {
                if (e && typeof e.stopPropagation === 'function') {
                  e.stopPropagation();
                }
              }}
            >
              <Row gutter={16}>
                <Col span={24}>
                  <div
                    style={{ marginBottom: 16 }}
                    onClick={e => {
                      if (e && typeof e.stopPropagation === 'function') {
                        e.stopPropagation();
                      }
                    }}
                    onMouseDown={e => {
                      if (e && typeof e.stopPropagation === 'function') {
                        e.stopPropagation();
                      }
                    }}
                    onMouseUp={e => {
                      if (e && typeof e.stopPropagation === 'function') {
                        e.stopPropagation();
                      }
                    }}
                  >
                    <Space>
                      <Text>下層ページも自動取得:</Text>
                      <Switch
                        checked={crawlSettings.crawl_subpages}
                        onChange={checked => {
                          setCrawlSettings(prev => ({
                            ...prev,
                            crawl_subpages: checked,
                          }));
                        }}
                        onClick={e => {
                          if (e && typeof e.stopPropagation === 'function') {
                            e.stopPropagation();
                            e.preventDefault();
                          }
                        }}
                        onMouseDown={e => {
                          if (e && typeof e.stopPropagation === 'function') {
                            e.stopPropagation();
                          }
                        }}
                        onMouseUp={e => {
                          if (e && typeof e.stopPropagation === 'function') {
                            e.stopPropagation();
                          }
                        }}
                      />
                    </Space>
                  </div>
                </Col>

                {crawlSettings.crawl_subpages && (
                  <>
                    <Col span={12}>
                      <div
                        onClick={e => {
                          if (e && typeof e.stopPropagation === 'function') {
                            e.stopPropagation();
                          }
                        }}
                        onMouseDown={e => {
                          if (e && typeof e.stopPropagation === 'function') {
                            e.stopPropagation();
                          }
                        }}
                        onMouseUp={e => {
                          if (e && typeof e.stopPropagation === 'function') {
                            e.stopPropagation();
                          }
                        }}
                      >
                        <Space>
                          <Text>階層数:</Text>
                          <InputNumber
                            min={1}
                            max={3}
                            value={crawlSettings.max_depth}
                            onChange={value => {
                              setCrawlSettings(prev => ({
                                ...prev,
                                max_depth: value,
                              }));
                            }}
                            onClick={e => {
                              if (
                                e &&
                                typeof e.stopPropagation === 'function'
                              ) {
                                e.stopPropagation();
                                e.preventDefault();
                              }
                            }}
                            onFocus={e => {
                              if (
                                e &&
                                typeof e.stopPropagation === 'function'
                              ) {
                                e.stopPropagation();
                              }
                            }}
                            onBlur={e => {
                              if (
                                e &&
                                typeof e.stopPropagation === 'function'
                              ) {
                                e.stopPropagation();
                              }
                            }}
                            onMouseDown={e => {
                              if (
                                e &&
                                typeof e.stopPropagation === 'function'
                              ) {
                                e.stopPropagation();
                              }
                            }}
                            onMouseUp={e => {
                              if (
                                e &&
                                typeof e.stopPropagation === 'function'
                              ) {
                                e.stopPropagation();
                              }
                            }}
                          />
                        </Space>
                      </div>
                    </Col>
                    <Col span={12}>
                      <div
                        onClick={e => {
                          if (e && typeof e.stopPropagation === 'function') {
                            e.stopPropagation();
                          }
                        }}
                        onMouseDown={e => {
                          if (e && typeof e.stopPropagation === 'function') {
                            e.stopPropagation();
                          }
                        }}
                        onMouseUp={e => {
                          if (e && typeof e.stopPropagation === 'function') {
                            e.stopPropagation();
                          }
                        }}
                      >
                        <Space>
                          <Text>上限数:</Text>
                          <InputNumber
                            min={1}
                            max={100}
                            value={crawlSettings.limit}
                            onChange={value => {
                              setCrawlSettings(prev => ({
                                ...prev,
                                limit: value,
                              }));
                            }}
                            onClick={e => {
                              if (
                                e &&
                                typeof e.stopPropagation === 'function'
                              ) {
                                e.stopPropagation();
                                e.preventDefault();
                              }
                            }}
                            onFocus={e => {
                              if (
                                e &&
                                typeof e.stopPropagation === 'function'
                              ) {
                                e.stopPropagation();
                              }
                            }}
                            onBlur={e => {
                              if (
                                e &&
                                typeof e.stopPropagation === 'function'
                              ) {
                                e.stopPropagation();
                              }
                            }}
                            onMouseDown={e => {
                              if (
                                e &&
                                typeof e.stopPropagation === 'function'
                              ) {
                                e.stopPropagation();
                              }
                            }}
                            onMouseUp={e => {
                              if (
                                e &&
                                typeof e.stopPropagation === 'function'
                              ) {
                                e.stopPropagation();
                              }
                            }}
                          />
                        </Space>
                      </div>
                    </Col>
                  </>
                )}
              </Row>
            </div>
          </Panel>
        </Collapse>

        <div style={{ marginTop: 16 }}>
          <Button
            type="primary"
            icon={<CloudDownloadOutlined />}
            onClick={handleUrlExtraction}
            loading={urlExtractionLoading}
            size="large"
          >
            URLからコンテンツを抽出
          </Button>
        </div>

        {extractionResults && (
          <UrlExtractionResults results={extractionResults} />
        )}
      </div>
    );
  };

  // リッチテキストセクション
  const RichTextSection = () => {
    const [content, setContent] = useState('');
    const textAreaRef = useRef(null);

    // テキスト挿入関数
    const insertText = useCallback(
      (before, after = '', placeholder = '') => {
        const textarea = textAreaRef.current?.resizableTextArea?.textArea;
        if (!textarea) return;

        const start = textarea.selectionStart;
        const end = textarea.selectionEnd;
        const selectedText = content.substring(start, end);
        const textToInsert = selectedText || placeholder;

        const newText =
          content.substring(0, start) +
          before +
          textToInsert +
          after +
          content.substring(end);
        setContent(newText);
        form.setFieldsValue({ content: newText });

        // カーソル位置を調整
        setTimeout(() => {
          const newCursorPos = start + before.length + textToInsert.length;
          textarea.focus();
          textarea.setSelectionRange(newCursorPos, newCursorPos);
        }, 0);
      },
      [content, form],
    );

    // ツールバーボタンのハンドラ
    const handleHeading = () => insertText('# ', '', '見出しテキスト');
    const handleBulletList = () => {
      const lines = content.split('\n');
      const textarea = textAreaRef.current?.resizableTextArea?.textArea;
      const start = textarea?.selectionStart || 0;

      // 現在の行を取得
      let currentLineStart = content.lastIndexOf('\n', start - 1) + 1;
      if (currentLineStart === 0 && start === 0) currentLineStart = 0;

      insertText('\n- ', '', '項目');
    };
    const handleLink = () => insertText('[', '](URL)', 'リンクテキスト');
    const handleImage = () => insertText('![', '](画像URL)', '画像の説明');

    const handleContentChange = useCallback(
      e => {
        const newContent = e.target.value;
        setContent(newContent);
        form.setFieldsValue({ content: newContent });
      },
      [form],
    );

    return (
      <div>
        <Form.Item
          label="コンテンツ"
          name="content"
          rules={[
            { required: true, message: 'コンテンツを入力してください' },
            { min: 10, message: 'コンテンツは10文字以上入力してください' },
          ]}
        >
          <div>
            {/* ツールバー */}
            <div
              style={{
                marginBottom: 8,
                padding: '8px 12px',
                backgroundColor: '#fafafa',
                border: '1px solid #d9d9d9',
                borderRadius: '6px 6px 0 0',
                borderBottom: 'none',
              }}
            >
              <Space size="small">
                <Tooltip title="見出し">
                  <Button
                    size="small"
                    icon={<FileTextOutlined />}
                    onClick={handleHeading}
                  >
                    見出し
                  </Button>
                </Tooltip>
                <Tooltip title="箇条書き">
                  <Button
                    size="small"
                    icon={
                      <div style={{ fontSize: '12px', fontWeight: 'bold' }}>
                        •
                      </div>
                    }
                    onClick={handleBulletList}
                  >
                    箇条書き
                  </Button>
                </Tooltip>
                <Tooltip title="リンク">
                  <Button
                    size="small"
                    icon={<LinkOutlined />}
                    onClick={handleLink}
                  >
                    リンク
                  </Button>
                </Tooltip>
                <Tooltip title="画像">
                  <Button
                    size="small"
                    icon={<FileImageOutlined />}
                    onClick={handleImage}
                  >
                    画像
                  </Button>
                </Tooltip>
              </Space>
            </div>

            {/* テキストエリア */}
            <TextArea
              ref={textAreaRef}
              rows={12}
              placeholder="ここにコンテンツを入力してください..."
              value={content}
              onChange={handleContentChange}
              style={{
                fontSize: '14px',
                lineHeight: '1.6',
                fontFamily: 'system-ui, -apple-system, sans-serif',
                borderRadius: '0 0 6px 6px',
              }}
              showCount
              maxLength={10000}
            />
          </div>
        </Form.Item>

        {/* 使い方ガイド */}
        <Alert
          message="💡 使い方"
          description="上のボタンをクリックして装飾を追加できます。テキストを選択してからボタンを押すと、選択したテキストが装飾されます。"
          type="info"
          showIcon={false}
          style={{
            marginBottom: 16,
            backgroundColor: '#f6ffed',
            border: '1px solid #b7eb8f',
          }}
        />
      </div>
    );
  };

  // ファイルプレビューセクション
  const FilePreviewSection = ({ preview }) => (
    <div style={{ marginTop: 24 }}>
      {/* ヘッダー部分 */}
      <div
        style={{
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          borderRadius: '12px 12px 0 0',
          padding: '20px 24px',
          color: 'white',
        }}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <div
              style={{
                width: 48,
                height: 48,
                borderRadius: '50%',
                background: 'rgba(255,255,255,0.2)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: 20,
              }}
            >
              📄
            </div>
            <div>
              <h3 style={{ margin: 0, fontSize: 18, fontWeight: 600 }}>
                ファイル解析完了
              </h3>
              <p style={{ margin: 0, opacity: 0.9, fontSize: 14 }}>
                {preview.file_info.filename}
              </p>
            </div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            {estimatedUnits && (
              <div
                style={{
                  background: 'rgba(255,255,255,0.25)',
                  borderRadius: 8,
                  padding: '8px 16px',
                  textAlign: 'center',
                }}
              >
                <div style={{ fontSize: 11, opacity: 0.9 }}>消費ユニット</div>
                <div style={{ fontSize: 18, fontWeight: 'bold' }}>
                  {estimatedUnits.units}
                </div>
                <div style={{ fontSize: 10, opacity: 0.8 }}>
                  {estimatedUnits.details}
                </div>
              </div>
            )}
            <div
              style={{
                background: 'rgba(255,255,255,0.2)',
                borderRadius: 8,
                padding: '8px 16px',
                fontSize: 12,
                fontWeight: 500,
              }}
            >
              ✅ 処理完了
            </div>
          </div>
        </div>
      </div>

      {/* メイン情報カード */}
      <div
        style={{
          background: 'white',
          borderRadius: '0 0 12px 12px',
          boxShadow: '0 8px 32px rgba(0,0,0,0.1)',
          overflow: 'hidden',
        }}
      >
        {/* 統計情報 */}
        <div style={{ padding: '24px 24px 0 24px' }}>
          <Row gutter={24}>
            <Col span={6}>
              <div style={{ textAlign: 'center', padding: '16px 0' }}>
                <div
                  style={{
                    fontSize: 28,
                    fontWeight: 700,
                    color: '#1890ff',
                    marginBottom: 4,
                  }}
                >
                  {Math.round((preview.file_info?.size_bytes || 0) / 1024)}
                </div>
                <Text type="secondary" style={{ fontSize: 12 }}>
                  ファイルサイズ(KB)
                </Text>
              </div>
            </Col>
            <Col span={6}>
              <div style={{ textAlign: 'center', padding: '16px 0' }}>
                <div
                  style={{
                    fontSize: 28,
                    fontWeight: 700,
                    color: '#52c41a',
                    marginBottom: 4,
                  }}
                >
                  {(
                    preview.content_stats?.content_length ||
                    preview.content_stats?.total_length ||
                    0
                  ).toLocaleString()}
                </div>
                <Text type="secondary" style={{ fontSize: 12 }}>
                  文字数
                </Text>
              </div>
            </Col>
            <Col span={6}>
              <div style={{ textAlign: 'center', padding: '16px 0' }}>
                <div
                  style={{
                    fontSize: 28,
                    fontWeight: 700,
                    color: '#faad14',
                    marginBottom: 4,
                  }}
                >
                  {preview.file_info?.type === 'pdf'
                    ? preview.content_stats?.total_pages ||
                      Math.ceil(
                        (preview.content_stats?.content_length || 0) / 500,
                      )
                    : preview.structured_data?.columns?.length ||
                      preview.content_stats?.total_columns ||
                      0}
                </div>
                <Text type="secondary" style={{ fontSize: 12 }}>
                  {preview.file_info?.type === 'pdf' ? 'ページ数' : '列数'}
                </Text>
              </div>
            </Col>
            <Col span={6}>
              <div style={{ textAlign: 'center', padding: '16px 0' }}>
                <div
                  style={{
                    fontSize: 28,
                    fontWeight: 700,
                    color: '#722ed1',
                    marginBottom: 4,
                  }}
                >
                  {preview.file_info?.type === 'pdf'
                    ? preview.content_stats?.line_count ||
                      (preview.content_stats?.content_length || '')
                        .toString()
                        .split('\n').length ||
                      0
                    : (
                        preview.structured_data?.total_rows ||
                        preview.content_stats?.total_rows ||
                        0
                      ).toLocaleString()}
                </div>
                <Text type="secondary" style={{ fontSize: 12 }}>
                  行数
                </Text>
              </div>
            </Col>
          </Row>
        </div>

        <Divider style={{ margin: '16px 0' }} />

        {/* プレビューエリア */}
        <div style={{ padding: '0 24px 24px 24px' }}>
          <div style={{ marginBottom: 16 }}>
            <Text strong style={{ fontSize: 16, color: '#262626' }}>
              コンテンツプレビュー
            </Text>
          </div>
          <div
            style={{
              background: '#fafafa',
              border: '2px dashed #d9d9d9',
              borderRadius: 8,
              padding: 20,
              maxHeight: 300,
              overflow: 'auto',
              fontFamily: 'Monaco, Consolas, "Courier New", monospace',
              fontSize: 13,
              lineHeight: 1.6,
              color: '#595959',
            }}
          >
            {preview.content_preview || 'プレビューを読み込み中...'}
          </div>
        </div>
      </div>
    </div>
  );

  // URL抽出結果セクション
  const UrlExtractionResults = ({ results }) => (
    <div style={{ marginTop: 24 }}>
      {/* ヘッダー部分 */}
      <div
        style={{
          background: 'linear-gradient(135deg, #13c2c2 0%, #1890ff 100%)',
          borderRadius: '12px 12px 0 0',
          padding: '20px 24px',
          color: 'white',
        }}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <div
              style={{
                width: 48,
                height: 48,
                borderRadius: '50%',
                background: 'rgba(255,255,255,0.2)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: 20,
              }}
            >
              🌐
            </div>
            <div>
              <h3 style={{ margin: 0, fontSize: 18, fontWeight: 600 }}>
                URL抽出完了
              </h3>
              <p style={{ margin: 0, opacity: 0.9, fontSize: 14 }}>
                {results.results.length}件のURLを処理しました
              </p>
            </div>
          </div>
          <div
            style={{
              background: 'rgba(255,255,255,0.2)',
              borderRadius: 8,
              padding: '8px 16px',
              fontSize: 12,
              fontWeight: 500,
            }}
          >
            ✅ 抽出完了
          </div>
        </div>
      </div>

      {/* メイン情報カード */}
      <div
        style={{
          background: 'white',
          borderRadius: '0 0 12px 12px',
          boxShadow: '0 8px 32px rgba(0,0,0,0.1)',
          overflow: 'hidden',
        }}
      >
        {/* 統計情報 */}
        <div style={{ padding: '24px 24px 0 24px' }}>
          <Row gutter={24}>
            <Col span={6}>
              <div style={{ textAlign: 'center', padding: '16px 0' }}>
                <div
                  style={{
                    fontSize: 28,
                    fontWeight: 700,
                    color: '#1890ff',
                    marginBottom: 4,
                  }}
                >
                  {results.summary.total_extracted_urls ||
                    results.results.length}
                </div>
                <Text type="secondary" style={{ fontSize: 12 }}>
                  発見URL数
                </Text>
              </div>
            </Col>
            <Col span={6}>
              <div style={{ textAlign: 'center', padding: '16px 0' }}>
                <div
                  style={{
                    fontSize: 28,
                    fontWeight: 700,
                    color: '#52c41a',
                    marginBottom: 4,
                  }}
                >
                  {results.summary.successful_extractions}
                </div>
                <Text type="secondary" style={{ fontSize: 12 }}>
                  抽出成功
                </Text>
              </div>
            </Col>
            <Col span={6}>
              <div style={{ textAlign: 'center', padding: '16px 0' }}>
                <div
                  style={{
                    fontSize: 28,
                    fontWeight: 700,
                    color: '#faad14',
                    marginBottom: 4,
                  }}
                >
                  {results.summary.skipped_duplicates || 0}
                </div>
                <Text type="secondary" style={{ fontSize: 12 }}>
                  重複スキップ
                </Text>
              </div>
            </Col>
            <Col span={6}>
              <div style={{ textAlign: 'center', padding: '16px 0' }}>
                <div
                  style={{
                    fontSize: 28,
                    fontWeight: 700,
                    color: '#ff4d4f',
                    marginBottom: 4,
                  }}
                >
                  {results.summary.failed_extractions}
                </div>
                <Text type="secondary" style={{ fontSize: 12 }}>
                  抽出失敗
                </Text>
              </div>
            </Col>
          </Row>
        </div>

        <Divider style={{ margin: '16px 0' }} />

        {/* URL一覧 */}
        {results.results.length > 0 && (
          <div style={{ padding: '0 24px 24px 24px' }}>
            <div style={{ marginBottom: 16 }}>
              <Text strong style={{ fontSize: 16, color: '#262626' }}>
                🔗 抽出されたURL一覧
              </Text>
            </div>
            <Table
              dataSource={results.results}
              columns={[
                {
                  title: 'URL',
                  dataIndex: 'source_url',
                  key: 'url',
                  ellipsis: true,
                  width: '70%',
                  render: url => (
                    <Tooltip title={url}>
                      <a href={url} target="_blank" rel="noopener noreferrer">
                        {url.length > 60 ? `${url.substring(0, 60)}...` : url}
                      </a>
                    </Tooltip>
                  ),
                },
                {
                  title: 'ステータス',
                  dataIndex: 'status',
                  key: 'status',
                  width: '30%',
                  render: status => {
                    if (status === 'success') {
                      return <Tag color="green">成功</Tag>;
                    } else if (status === 'skipped_duplicate') {
                      return <Tag color="orange">重複スキップ</Tag>;
                    } else {
                      return <Tag color="red">失敗</Tag>;
                    }
                  },
                },
              ]}
              size="small"
              pagination={false}
              rowKey="source_url"
            />
          </div>
        )}
      </div>
    </div>
  );

  // ステップ3: 確認
  const ConfirmationStep = () => {
    const formValues = form.getFieldsValue();

    // ソースタイプ別の確認内容を生成
    const renderConfirmationContent = () => {
      switch (sourceType) {
        case 'url':
          return (
            <div style={{ maxWidth: 800, margin: '0 auto' }}>
              {/* ヘッダー */}
              <div
                style={{
                  background:
                    'linear-gradient(135deg, #13c2c2 0%, #1890ff 100%)',
                  borderRadius: '12px 12px 0 0',
                  padding: '24px',
                  color: 'white',
                  textAlign: 'center',
                }}
              >
                <h2 style={{ margin: 0, fontSize: 24, fontWeight: 600 }}>
                  URL抽出結果の確認
                </h2>
                <p style={{ margin: '8px 0 0', opacity: 0.9 }}>
                  {extractionResults?.results?.length || 0}件のURLを処理しました
                </p>
              </div>

              {/* コンテンツ */}
              <div
                style={{
                  background: 'white',
                  borderRadius: '0 0 12px 12px',
                  boxShadow: '0 8px 32px rgba(0,0,0,0.1)',
                  padding: '24px',
                }}
              >
                <BasicInfoSection
                  sourceType={sourceType}
                  formValues={formValues}
                />
                <div
                  style={{
                    background: '#fafafa',
                    borderRadius: 8,
                    padding: '16px 0',
                    maxHeight: 400,
                    overflow: 'auto',
                  }}
                >
                  {(extractionResults?.results || []).map((item, index) => (
                    <div
                      key={item.source_url}
                      style={{
                        background: 'white',
                        margin: '0 16px 12px 16px',
                        padding: 16,
                        borderRadius: 8,
                        boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
                        border: '1px solid #f0f0f0',
                      }}
                    >
                      <div
                        style={{
                          display: 'flex',
                          alignItems: 'flex-start',
                          gap: 12,
                        }}
                      >
                        <div
                          style={{
                            width: 32,
                            height: 32,
                            borderRadius: '50%',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontSize: 14,
                            fontWeight: 600,
                            background:
                              item.status === 'success'
                                ? '#f6ffed'
                                : item.status === 'skipped_duplicate'
                                  ? '#fff7e6'
                                  : '#fff2f0',
                            color:
                              item.status === 'success'
                                ? '#52c41a'
                                : item.status === 'skipped_duplicate'
                                  ? '#faad14'
                                  : '#ff4d4f',
                          }}
                        >
                          {item.status === 'success'
                            ? '✓'
                            : item.status === 'skipped_duplicate'
                              ? '⚠'
                              : '✗'}
                        </div>

                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 8,
                              marginBottom: 4,
                            }}
                          >
                            <Text strong style={{ fontSize: 14 }}>
                              {item.title}
                            </Text>
                            <Tag
                              color={
                                item.status === 'success'
                                  ? 'green'
                                  : item.status === 'skipped_duplicate'
                                    ? 'orange'
                                    : 'red'
                              }
                              size="small"
                            >
                              {item.status === 'success'
                                ? '成功'
                                : item.status === 'skipped_duplicate'
                                  ? '重複スキップ'
                                  : '失敗'}
                            </Tag>
                          </div>

                          <Text
                            type="secondary"
                            style={{
                              fontSize: 12,
                              display: 'block',
                              marginBottom: 8,
                              wordBreak: 'break-all',
                            }}
                          >
                            {item.source_url}
                          </Text>

                          {item.status === 'skipped_duplicate' && (
                            <div
                              style={{
                                background: '#fff7e6',
                                borderRadius: 4,
                                padding: '6px 10px',
                                fontSize: 11,
                              }}
                            >
                              <Text type="secondary">
                                既存データと重複しています
                              </Text>
                            </div>
                          )}

                          {item.status === 'error' && (
                            <div
                              style={{
                                background: '#fff2f0',
                                borderRadius: 4,
                                padding: '6px 10px',
                                fontSize: 11,
                              }}
                            >
                              <Text type="danger">
                                {item.error_reason ||
                                  item.error ||
                                  'エラーが発生しました'}
                              </Text>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          );

        case 'csv': {
          // CSVデータをテーブル形式で表示
          const csvTableData = previewData?.structured_data?.rows || [];
          const csvTableColumns =
            previewData?.structured_data?.columns?.map(col => ({
              title: col,
              dataIndex: col,
              key: col,
              ellipsis: true,
              width: 150,
            })) || [];

          return (
            <div style={{ maxWidth: 1000, margin: '0 auto' }}>
              {/* ヘッダー */}
              <div
                style={{
                  background:
                    'linear-gradient(135deg, #13c2c2 0%, #1890ff 100%)',
                  borderRadius: '12px 12px 0 0',
                  padding: '24px',
                  color: 'white',
                  textAlign: 'center',
                }}
              >
                <h2 style={{ margin: 0, fontSize: 24, fontWeight: 600 }}>
                  CSVファイルの確認
                </h2>
                <p style={{ margin: '8px 0 0', opacity: 0.9 }}>
                  {previewData?.file_info?.filename || 'CSVファイル'}
                </p>
              </div>

              {/* コンテンツ */}
              <div
                style={{
                  background: 'white',
                  borderRadius: '0 0 12px 12px',
                  border: '1px solid #e8f4f8',
                  borderTop: 'none',
                }}
              >
                {/* 統計情報 */}
                <div
                  style={{
                    padding: '20px 24px',
                    borderBottom: '1px solid #f0f0f0',
                    background: '#fafbfc',
                  }}
                >
                  <Row gutter={[24, 16]}>
                    <Col span={6}>
                      <div style={{ textAlign: 'center', padding: '16px 0' }}>
                        <div
                          style={{
                            fontSize: 28,
                            fontWeight: 700,
                            color: '#1890ff',
                            marginBottom: 4,
                          }}
                        >
                          {Math.round(
                            (previewData?.file_info?.size_bytes || 0) / 1024,
                          )}
                        </div>
                        <Text type="secondary" style={{ fontSize: 12 }}>
                          ファイルサイズ(KB)
                        </Text>
                      </div>
                    </Col>
                    <Col span={6}>
                      <div style={{ textAlign: 'center', padding: '16px 0' }}>
                        <div
                          style={{
                            fontSize: 28,
                            fontWeight: 700,
                            color: '#52c41a',
                            marginBottom: 4,
                          }}
                        >
                          {(
                            previewData?.content_stats?.content_length ||
                            previewData?.content_stats?.total_length ||
                            0
                          ).toLocaleString()}
                        </div>
                        <Text type="secondary" style={{ fontSize: 12 }}>
                          文字数
                        </Text>
                      </div>
                    </Col>
                    <Col span={6}>
                      <div style={{ textAlign: 'center', padding: '16px 0' }}>
                        <div
                          style={{
                            fontSize: 28,
                            fontWeight: 700,
                            color: '#faad14',
                            marginBottom: 4,
                          }}
                        >
                          {previewData?.structured_data?.columns?.length ||
                            previewData?.content_stats?.total_columns ||
                            csvTableColumns.length ||
                            0}
                        </div>
                        <Text type="secondary" style={{ fontSize: 12 }}>
                          列数
                        </Text>
                      </div>
                    </Col>
                    <Col span={6}>
                      <div style={{ textAlign: 'center', padding: '16px 0' }}>
                        <div
                          style={{
                            fontSize: 28,
                            fontWeight: 700,
                            color: '#722ed1',
                            marginBottom: 4,
                          }}
                        >
                          {(
                            previewData?.structured_data?.total_rows ||
                            previewData?.content_stats?.total_rows ||
                            csvTableData.length ||
                            0
                          ).toLocaleString()}
                        </div>
                        <Text type="secondary" style={{ fontSize: 12 }}>
                          行数
                        </Text>
                      </div>
                    </Col>
                  </Row>
                </div>

                {/* 基本情報とプレビューエリア */}
                <div style={{ padding: '0 24px 24px 24px' }}>
                  <BasicInfoSection
                    sourceType={sourceType}
                    formValues={formValues}
                  />
                  <div style={{ marginBottom: 16 }}>
                    <Text strong style={{ fontSize: 16, color: '#262626' }}>
                      データプレビュー
                    </Text>
                  </div>

                  {csvTableData.length > 0 ? (
                    <div
                      style={{
                        background: '#fafafa',
                        borderRadius: 8,
                        padding: '16px 0',
                        maxHeight: 400,
                        overflow: 'auto',
                      }}
                    >
                      <Table
                        dataSource={csvTableData.slice(0, 10)} // 最大10行表示
                        columns={csvTableColumns}
                        pagination={false}
                        size="small"
                        scroll={{ x: true }}
                        rowKey={(record, index) => index}
                        style={{ margin: '0 16px' }}
                      />
                      {csvTableData.length > 10 && (
                        <div
                          style={{
                            textAlign: 'center',
                            marginTop: 12,
                            color: '#666',
                          }}
                        >
                          <Text type="secondary">
                            上位10行を表示中（全{csvTableData.length}行）
                          </Text>
                        </div>
                      )}
                    </div>
                  ) : (
                    <div
                      style={{
                        textAlign: 'center',
                        padding: '40px 20px',
                        color: '#999',
                        background: '#fafafa',
                        borderRadius: 8,
                      }}
                    >
                      <Text type="secondary">データが見つかりませんでした</Text>
                    </div>
                  )}
                </div>
              </div>
            </div>
          );
        }

        case 'text': {
          const content = formValues.content || '';
          const wordCount = content
            .split(/\s+/)
            .filter(word => word.length > 0).length;
          const lineCount = content.split('\n').length;

          return (
            <div style={{ maxWidth: 800, margin: '0 auto' }}>
              {/* ヘッダー */}
              <div
                style={{
                  background:
                    'linear-gradient(135deg, #13c2c2 0%, #1890ff 100%)',
                  borderRadius: '12px 12px 0 0',
                  padding: '24px',
                  color: 'white',
                  textAlign: 'center',
                }}
              >
                <h2 style={{ margin: 0, fontSize: 24, fontWeight: 600 }}>
                  テキストコンテンツの確認
                </h2>
                <p style={{ margin: '8px 0 0', opacity: 0.9 }}>
                  入力されたテキストを確認してください
                </p>
              </div>

              {/* コンテンツ */}
              <div
                style={{
                  background: 'white',
                  borderRadius: '0 0 12px 12px',
                  boxShadow: '0 8px 32px rgba(0,0,0,0.1)',
                  overflow: 'hidden',
                }}
              >
                {/* 統計情報 */}
                <div style={{ padding: '24px 24px 0 24px' }}>
                  <Row gutter={24}>
                    <Col span={12}>
                      <div style={{ textAlign: 'center', padding: '16px 0' }}>
                        <div
                          style={{
                            fontSize: 28,
                            fontWeight: 700,
                            color: '#1890ff',
                            marginBottom: 4,
                          }}
                        >
                          {(content?.length || 0).toLocaleString()}
                        </div>
                        <Text type="secondary" style={{ fontSize: 12 }}>
                          文字数
                        </Text>
                      </div>
                    </Col>
                    <Col span={12}>
                      <div style={{ textAlign: 'center', padding: '16px 0' }}>
                        <div
                          style={{
                            fontSize: 28,
                            fontWeight: 700,
                            color: '#722ed1',
                            marginBottom: 4,
                          }}
                        >
                          {(lineCount || 0).toLocaleString()}
                        </div>
                        <Text type="secondary" style={{ fontSize: 12 }}>
                          行数
                        </Text>
                      </div>
                    </Col>
                  </Row>
                </div>

                <Divider style={{ margin: '16px 0' }} />

                {/* 基本情報とプレビューエリア */}
                <div style={{ padding: '0 24px 24px 24px' }}>
                  <BasicInfoSection
                    sourceType={sourceType}
                    formValues={formValues}
                  />
                  <div style={{ marginBottom: 16 }}>
                    <Text strong style={{ fontSize: 16, color: '#262626' }}>
                      コンテンツプレビュー
                    </Text>
                  </div>
                  <div
                    style={{
                      background: '#fafafa',
                      border: '2px dashed #d9d9d9',
                      borderRadius: 8,
                      padding: 20,
                      maxHeight: 400,
                      overflow: 'auto',
                      fontFamily: 'Monaco, Consolas, "Courier New", monospace',
                      fontSize: 13,
                      lineHeight: 1.6,
                      color: '#595959',
                      whiteSpace: 'pre-wrap',
                    }}
                  >
                    {content && content.length > 500
                      ? content.substring(0, 500) + '...'
                      : content}
                  </div>
                  {content && content.length > 500 && (
                    <div
                      style={{
                        textAlign: 'center',
                        marginTop: 16,
                        padding: '12px',
                        background: '#f0f0f0',
                        borderRadius: 6,
                        color: '#666',
                      }}
                    >
                      📄 残り {((content?.length || 0) - 500).toLocaleString()}
                      文字のテキストがあります
                    </div>
                  )}
                </div>
              </div>
            </div>
          );
        }

        case 'pdf':
          return (
            <div style={{ maxWidth: 800, margin: '0 auto' }}>
              {/* ヘッダー */}
              <div
                style={{
                  background:
                    'linear-gradient(135deg, #13c2c2 0%, #1890ff 100%)',
                  borderRadius: '12px 12px 0 0',
                  padding: '24px',
                  color: 'white',
                  textAlign: 'center',
                }}
              >
                <h2 style={{ margin: 0, fontSize: 24, fontWeight: 600 }}>
                  PDFファイルの確認
                </h2>
                <p style={{ margin: '8px 0 0', opacity: 0.9 }}>
                  {previewData?.file_info?.filename || 'PDFファイル'}
                </p>
              </div>

              {/* コンテンツ */}
              <div
                style={{
                  background: 'white',
                  borderRadius: '0 0 12px 12px',
                  border: '1px solid #e8f4f8',
                  borderTop: 'none',
                }}
              >
                {/* 統計情報 */}
                <div
                  style={{
                    padding: '20px 24px',
                    borderBottom: '1px solid #f0f0f0',
                    background: '#fafbfc',
                  }}
                >
                  <Row gutter={[24, 16]}>
                    <Col span={6}>
                      <div style={{ textAlign: 'center', padding: '16px 0' }}>
                        <div
                          style={{
                            fontSize: 28,
                            fontWeight: 700,
                            color: '#1890ff',
                            marginBottom: 4,
                          }}
                        >
                          {Math.round(
                            (previewData?.file_info?.size_bytes || 0) / 1024,
                          )}
                        </div>
                        <Text type="secondary" style={{ fontSize: 12 }}>
                          ファイルサイズ(KB)
                        </Text>
                      </div>
                    </Col>
                    <Col span={6}>
                      <div style={{ textAlign: 'center', padding: '16px 0' }}>
                        <div
                          style={{
                            fontSize: 28,
                            fontWeight: 700,
                            color: '#52c41a',
                            marginBottom: 4,
                          }}
                        >
                          {(
                            previewData?.content_stats?.content_length ||
                            previewData?.content_stats?.total_length ||
                            0
                          ).toLocaleString()}
                        </div>
                        <Text type="secondary" style={{ fontSize: 12 }}>
                          文字数
                        </Text>
                      </div>
                    </Col>
                    <Col span={6}>
                      <div style={{ textAlign: 'center', padding: '16px 0' }}>
                        <div
                          style={{
                            fontSize: 28,
                            fontWeight: 700,
                            color: '#faad14',
                            marginBottom: 4,
                          }}
                        >
                          {previewData?.content_stats?.total_pages ||
                            Math.ceil(
                              (previewData?.content_stats?.content_length ||
                                0) / 500,
                            ) ||
                            0}
                        </div>
                        <Text type="secondary" style={{ fontSize: 12 }}>
                          ページ数
                        </Text>
                      </div>
                    </Col>
                    <Col span={6}>
                      <div style={{ textAlign: 'center', padding: '16px 0' }}>
                        <div
                          style={{
                            fontSize: 28,
                            fontWeight: 700,
                            color: '#722ed1',
                            marginBottom: 4,
                          }}
                        >
                          {(
                            previewData?.content_stats?.line_count ||
                            previewData?.content_stats?.content_length
                              ?.toString()
                              .split('\n').length ||
                            0
                          ).toLocaleString()}
                        </div>
                        <Text type="secondary" style={{ fontSize: 12 }}>
                          行数
                        </Text>
                      </div>
                    </Col>
                  </Row>
                </div>

                {/* 基本情報とプレビューエリア */}
                <div style={{ padding: '0 24px 24px 24px' }}>
                  <BasicInfoSection
                    sourceType={sourceType}
                    formValues={formValues}
                  />
                  <div style={{ marginBottom: 16 }}>
                    <Text strong style={{ fontSize: 16, color: '#262626' }}>
                      コンテンツプレビュー
                    </Text>
                  </div>

                  <div
                    style={{
                      background: '#fafafa',
                      borderRadius: 8,
                      padding: 16,
                      maxHeight: 400,
                      overflow: 'auto',
                    }}
                  >
                    <Text
                      style={{
                        fontSize: 14,
                        lineHeight: 1.6,
                        color: '#262626',
                        whiteSpace: 'pre-wrap',
                      }}
                    >
                      {previewData?.structured_data?.content_preview ||
                        previewData?.raw_content?.substring(0, 1000) + '...' ||
                        'プレビューデータがありません'}
                    </Text>
                  </div>
                </div>
              </div>
            </div>
          );

        default:
          return null;
      }
    };

    return (
      <div style={{ padding: '24px 0' }}>
        {renderConfirmationContent()}

        {/* ユニット消費情報 */}
        {estimatedUnits && (
          <Card
            title={
              <span>
                <InfoCircleOutlined style={{ marginRight: 8 }} />
                消費ユニット
              </span>
            }
            style={{ marginTop: 16 }}
          >
            <div
              style={{
                background: 'linear-gradient(135deg, #f5f0ff 0%, #efe6ff 100%)',
                padding: 20,
                borderRadius: 8,
                border: '1px solid #d3c4e3',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
              }}
            >
              <div>
                <div
                  style={{ fontSize: 14, color: '#595959', marginBottom: 8 }}
                >
                  この独自情報を登録すると消費するユニット数
                </div>
                <div style={{ fontSize: 12, color: '#8c8c8c' }}>
                  {estimatedUnits.details}
                </div>
              </div>
              <div
                style={{
                  background:
                    'linear-gradient(135deg, #722ed1 0%, #9254de 100%)',
                  color: 'white',
                  borderRadius: 12,
                  padding: '16px 28px',
                  textAlign: 'center',
                }}
              >
                <div
                  style={{ fontSize: 32, fontWeight: 'bold', lineHeight: 1 }}
                >
                  {estimatedUnits.units}
                </div>
                <div style={{ fontSize: 12, opacity: 0.9, marginTop: 4 }}>
                  ユニット
                </div>
              </div>
            </div>
            <div style={{ marginTop: 12, fontSize: 12, color: '#8c8c8c' }}>
              <InfoCircleOutlined style={{ marginRight: 4 }} />
              ユニット計算: URL=1 / CSV=100行ごと / PDF=5ページごと /
              TEXT=5000文字ごと
            </div>
          </Card>
        )}

        <Alert
          message="登録の準備が完了しました"
          description="「登録」ボタンをクリックして独自情報を保存してください。"
          type="success"
          showIcon
          style={{ marginTop: 24 }}
        />
      </div>
    );
  };

  // ステップ定義（設定完了ステップを削除）
  const steps = [
    {
      title: 'ソース選択',
      content: <SourceTypeStep />,
    },
    {
      title: '基本情報',
      content: <BasicInfoStep />,
    },
    {
      title: '確認',
      content: <ConfirmationStep />,
    },
  ];

  // ステップバリデーション関数（簡素化）
  const validateStep = async (step, data) => {
    return {
      data: {
        valid: true,
        next_step:
          step === 'source_type'
            ? 'basic_info'
            : step === 'basic_info'
              ? 'confirmation'
              : 'confirmation',
      },
    };
  };

  // 次へボタンハンドラ
  const handleNext = async () => {
    try {
      if (currentStep === 0) {
        // ステップ1: ソースタイプ選択の確認
        if (!sourceType) {
          message.error('ソースタイプを選択してください');
          return;
        }
      } else if (currentStep === 1) {
        // ステップ2: 基本情報とソース別必須項目の確認
        // URLタイプの場合はタイトルフィールドを除外してバリデーション
        if (sourceType === 'url') {
          await form.validateFields([
            'category',
            'tags',
            'description',
            'urls',
          ]);
        } else {
          await form.validateFields();
        }

        // ソースタイプ別の必須チェック
        if (sourceType === 'csv' || sourceType === 'pdf') {
          if (!previewData) {
            message.error('ファイルをアップロードして解析を完了してください');
            return;
          }
        } else if (sourceType === 'url') {
          if (!extractionResults) {
            message.error('URLからのコンテンツ抽出を完了してください');
            return;
          }
          if (extractionResults.summary.successful_extractions === 0) {
            message.error('有効なURLからのコンテンツ抽出が必要です');
            return;
          }
        } else if (sourceType === 'text') {
          const content = form.getFieldValue('content');
          if (!content || content.trim().length < 10) {
            message.error('コンテンツを10文字以上入力してください');
            return;
          }
        }
      }

      if (currentStep < steps.length - 1) {
        setCurrentStep(currentStep + 1);

        // 確認STEPに進んだときにモーダルを上までスムーススクロール
        if (currentStep + 1 === steps.length - 1) {
          // 確認STEPに進む場合
          scrollToTop();
        }
      } else {
        // 最終登録
        handleSubmit();
      }
    } catch (error) {
      message.error('入力内容に不備があります');
      // エラー時も上にスクロールして問題箇所を確認しやすくする
      scrollToTop();
    }
  };

  // 戻るボタンハンドラ
  const handlePrev = () => {
    setCurrentStep(currentStep - 1);
  };

  // 最終登録処理
  const handleSubmit = async () => {
    try {
      setLoading(true);
      const formValues = form.getFieldsValue();

      console.log('=== DEBUG: Form Values ===', formValues);
      console.log('=== DEBUG: Form Data State ===', formData);
      console.log('=== DEBUG: Source Type ===', sourceType);
      console.log('=== DEBUG: Preview Data ===', previewData);
      console.log('=== DEBUG: Extraction Results ===', extractionResults);

      // 送信時にフォーム値を個別取得（getFieldsValue()が空になる問題回避）
      const currentFormValues = {
        title: form.getFieldValue('title') || '',
        description: form.getFieldValue('description') || '',
        category: form.getFieldValue('category') || '',
        tags: form.getFieldValue('tags') || [],
        content: form.getFieldValue('content') || '',
        strength: form.getFieldValue('strength') || 1.0,
        chunk_size: form.getFieldValue('chunk_size') || 1000,
        chunk_overlap: form.getFieldValue('chunk_overlap') || 200,
      };
      console.log(
        '=== DEBUG: Current Form Values (Individual) ===',
        currentFormValues,
      );

      // フォームデータの検証とデフォルト値設定
      let title = '';
      if (sourceType === 'url') {
        // URLタイプの場合は抽出結果からタイトルを取得（後で個別設定）
        title = 'URL抽出データ'; // 仮のタイトル（実際は各URLごとに設定）
      } else {
        title =
          currentFormValues.title ||
          formValues.title ||
          previewData?.title ||
          '';
      }

      const content =
        sourceType === 'text'
          ? currentFormValues.content || formValues.content || ''
          : previewData?.raw_content || '';

      console.log('=== DEBUG: Final Title ===', title);
      console.log('=== DEBUG: Final Content Length ===', content?.length || 0);

      // デバッグ用：全フィールドの状態確認
      console.log('=== DEBUG: All Form Fields Debug ===', {
        'form.getFieldValue(title)': form.getFieldValue('title'),
        'form.getFieldValue(description)': form.getFieldValue('description'),
        'form.getFieldValue(category)': form.getFieldValue('category'),
        'form.getFieldValue(tags)': form.getFieldValue('tags'),
        'sourceType': sourceType,
        'previewData.title': previewData?.title,
      });

      // URLタイプ以外でタイトルチェック
      if (sourceType !== 'url' && !title) {
        message.error('タイトルを入力してください');
        return;
      }

      if (sourceType === 'text' && !content) {
        message.error('コンテンツを入力してください');
        return;
      }

      // ソースタイプに応じた処理
      if (sourceType === 'text') {
        // テキスト系の場合：直接登録
        const submitData = {
          title: title,
          description:
            currentFormValues.description || formValues.description || '',
          type: sourceType,
          content: content,
          structured_data: null,
          content_metadata: {
            content_length: content?.length || 0,
            created_at: new Date().toISOString(),
          },
          category: currentFormValues.category || formValues.category || '',
          tags: currentFormValues.tags || formValues.tags || [],
          chunk_size:
            currentFormValues.chunk_size || formValues.chunk_size || 1000,
          chunk_overlap:
            currentFormValues.chunk_overlap || formValues.chunk_overlap || 200,
          reliability: currentFormValues.strength || formValues.strength || 1.0,
          is_active: true,
        };

        console.log('=== DEBUG: Text Submit Data ===', submitData);

        const response = await v2ToppingsApi.createTopping(submitData);
        console.log('登録結果:', response.data);
      } else if (sourceType === 'csv' || sourceType === 'pdf') {
        // ファイル系の場合：プレビューデータから最終登録
        if (!previewData || !previewData.raw_content) {
          throw new Error('ファイルの解析データが見つかりません');
        }

        const submitData = {
          title: title,
          description:
            currentFormValues.description || formValues.description || '',
          type: sourceType,
          content: previewData.raw_content,
          structured_data: previewData.structured_data,
          content_metadata: {
            filename: previewData.file_info?.filename,
            file_size: previewData.file_info?.size_bytes,
            file_type: previewData.file_info?.type,
            processed_at: previewData.file_info?.processed_at,
          },
          category: currentFormValues.category || formValues.category || '',
          tags: currentFormValues.tags || formValues.tags || [],
          chunk_size:
            currentFormValues.chunk_size || formValues.chunk_size || 1000,
          chunk_overlap:
            currentFormValues.chunk_overlap || formValues.chunk_overlap || 200,
          reliability: currentFormValues.strength || formValues.strength || 1.0,
          is_active: true,
        };

        console.log('=== DEBUG: File Submit Data ===', submitData);
        console.log('=== DEBUG: Category and Tags ===', {
          category: submitData.category,
          tags: submitData.tags,
          categoryType: typeof submitData.category,
          tagsType: typeof submitData.tags,
          tagsLength: submitData.tags?.length,
        });

        const response = await v2ToppingsApi.createTopping(submitData);
        console.log('ファイル登録結果:', response.data);
      } else if (sourceType === 'url') {
        // URL系の場合：抽出結果から登録
        if (!extractionResults) {
          throw new Error('URL抽出データが見つかりません');
        }

        // 成功したURL抽出結果のみを登録
        const successfulExtractions = extractionResults.results.filter(
          result => result.status === 'success',
        );

        if (successfulExtractions.length === 0) {
          throw new Error('登録可能なURL抽出データがありません');
        }

        // 複数のURL抽出結果を一括登録
        const registrationPromises = successfulExtractions.map(
          async extraction => {
            const submitData = {
              title: extraction.title || extraction.source_url,
              description:
                extraction.description ||
                currentFormValues.description ||
                formValues.description ||
                '',
              type: 'url',
              content: extraction.content,
              structured_data: {
                url: extraction.source_url,
                title: extraction.title,
                description: extraction.description,
                keywords: extraction.keywords,
                headings: extraction.headings,
              },
              content_metadata: {
                url: extraction.source_url,
                extracted_at: extraction.extracted_at,
                content_length: extraction.content?.length || 0,
              },
              category: currentFormValues.category || formValues.category || '',
              tags: currentFormValues.tags || formValues.tags || [],
              source_url: extraction.source_url,
              chunk_size:
                currentFormValues.chunk_size || formValues.chunk_size || 1000,
              chunk_overlap:
                currentFormValues.chunk_overlap ||
                formValues.chunk_overlap ||
                200,
              reliability:
                currentFormValues.strength || formValues.strength || 1.0,
              is_active: true,
            };

            console.log('=== DEBUG: URL Submit Data ===', submitData);

            const response = await v2ToppingsApi.createTopping(submitData);
            return response.data;
          },
        );

        const results = await Promise.all(registrationPromises);
        console.log('URL一括登録結果:', results);
      }

      message.success('独自情報が正常に登録されました');
      onSuccess();
      handleClose();
    } catch (error) {
      console.error('Submit error:', error);
      message.error(`登録に失敗しました: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  // 基本情報表示コンポーネント
  const BasicInfoSection = ({ sourceType, formValues }) => {
    // 最新のフォーム値を個別取得
    const displayData = {
      title: form.getFieldValue('title') || formValues.title || '',
      category: form.getFieldValue('category') || formValues.category || '',
      tags: form.getFieldValue('tags') || formValues.tags || [],
      description:
        form.getFieldValue('description') || formValues.description || '',
    };

    // URLタイプ以外の場合はタイトルもチェック、URLタイプの場合はタイトル以外をチェック
    const hasBasicInfo =
      sourceType === 'url'
        ? displayData.category ||
          (displayData.tags && displayData.tags.length > 0) ||
          displayData.description
        : displayData.title ||
          displayData.category ||
          (displayData.tags && displayData.tags.length > 0) ||
          displayData.description;

    if (!hasBasicInfo) {
      return null;
    }

    return (
      <div style={{ marginBottom: 16, marginTop: 24 }}>
        <Text
          strong
          style={{
            fontSize: 16,
            color: '#262626',
            marginBottom: 12,
            display: 'block',
          }}
        >
          基本情報
        </Text>
        <div
          style={{
            background: '#f8f9fa',
            borderRadius: 8,
            padding: 16,
            border: '1px solid #e9ecef',
          }}
        >
          {sourceType !== 'url' && displayData.title && (
            <div style={{ marginBottom: 8 }}>
              <Text style={{ fontSize: 13, color: '#666' }}>タイトル</Text>
              <div style={{ fontSize: 14, fontWeight: 500, color: '#262626' }}>
                {displayData.title}
              </div>
            </div>
          )}

          {displayData.category && (
            <div style={{ marginBottom: 8 }}>
              <Text style={{ fontSize: 13, color: '#666' }}>カテゴリー</Text>
              <div style={{ fontSize: 14, fontWeight: 500, color: '#262626' }}>
                {getCategoryDisplayName(displayData.category)}
              </div>
            </div>
          )}

          {displayData.tags && displayData.tags.length > 0 && (
            <div style={{ marginBottom: 8 }}>
              <Text style={{ fontSize: 13, color: '#666' }}>タグ</Text>
              <div style={{ marginTop: 4 }}>
                {displayData.tags.map(tag => (
                  <Tag
                    key={tag}
                    color="blue"
                    size="small"
                    style={{ margin: '2px' }}
                  >
                    {tag}
                  </Tag>
                ))}
              </div>
            </div>
          )}

          {displayData.description && (
            <div>
              <Text style={{ fontSize: 13, color: '#666' }}>説明</Text>
              <div style={{ fontSize: 14, color: '#262626', marginTop: 4 }}>
                {displayData.description}
              </div>
            </div>
          )}
        </div>
      </div>
    );
  };

  return (
    <Modal
      title="独自情報の新規追加"
      open={visible}
      onCancel={handleClose}
      footer={null}
      width={900}
      destroyOnClose
      style={{ top: 20 }}
      styles={{
        body: {
          maxHeight: 'calc(100vh - 120px)',
          overflowY: 'auto',
          padding: '20px 0',
        },
      }}
      className="topping-wizard-modal"
    >
      <div ref={modalBodyRef} style={{ padding: '20px 24px 0' }}>
        <Steps
          current={currentStep}
          style={{
            marginBottom: 32,
          }}
        >
          {steps.map((step, index) => (
            <Step
              key={index}
              title={
                <span
                  style={{
                    fontSize: '14px',
                    color: index <= currentStep ? '#1890ff' : '#999',
                    fontWeight: index === currentStep ? 'bold' : 'normal',
                  }}
                >
                  {step.title}
                </span>
              }
              disabled={index > currentStep}
            />
          ))}
        </Steps>

        <div style={{ minHeight: 400 }}>{steps[currentStep].content}</div>

        <div style={{ marginTop: 24 }}>
          <Row gutter={16}>
            {currentStep > 0 && (
              <Col span={12}>
                <Button
                  size="large"
                  onClick={handlePrev}
                  style={{ width: '100%' }}
                >
                  戻る
                </Button>
              </Col>
            )}
            <Col span={currentStep > 0 ? 12 : 24}>
              <Button
                type="primary"
                size="large"
                onClick={handleNext}
                loading={loading}
                style={{ width: '100%' }}
                disabled={
                  (currentStep === 0 && !sourceType) ||
                  (currentStep === 1 &&
                    sourceType === 'url' &&
                    !extractionResults) ||
                  (currentStep === 1 &&
                    (sourceType === 'csv' || sourceType === 'pdf') &&
                    !previewData)
                }
              >
                {currentStep === steps.length - 1 ? '登録' : '次へ'}
              </Button>
            </Col>
          </Row>
        </div>
      </div>

      <style jsx>{`
        .selected-source-type {
          box-shadow: 0 4px 12px rgba(24, 144, 255, 0.3);
        }
      `}</style>
    </Modal>
  );
};

export default AddToppingWizard;
